import { z } from "zod";

import { OfferAutoDetectPlugin } from "./editor/OfferAutoDetectPlugin";
import { OfferNode } from "./editor/OfferNode";
import { OfferPlugin } from "./editor/OfferPlugin";
import { OffersSection } from "./OffersSection";
import type { TextComposerExtension } from "../../editor/extensionContract";

import { buildOfferConfigSlice } from "@/frames/service";
import { buildOfferTag } from "@/grapesPlugins/email/OfferBlock/offerUtils";

export interface TextComposerOfferSlice {
  slug: string;
  fallbackCode: string;
}

declare module "../../editor/authoringState" {
  interface TextComposerAuthoringState {
    offer?: TextComposerOfferSlice;
  }
}

const OFFER_OWNED_KEY = "offer";

const offerSliceSchema = z.object({
  slug: z.string(),
  fallbackCode: z.string(),
});

function isOfferSliceEmpty(value: TextComposerOfferSlice | undefined): boolean {
  if (!value) return true;
  return !value.slug.trim();
}

function replaceOfferPlaceholders(text: string, slug: string): string {
  const tag = buildOfferTag(slug);
  return text.replaceAll("$OFFER", tag);
}

export const offerExtension: TextComposerExtension<TextComposerOfferSlice> = {
  id: "offers",

  contentContribution: (state) => {
    if (!state.offer?.slug?.trim()) return null;
    return { text: true };
  },

  stateSchema: offerSliceSchema,

  isEmpty: isOfferSliceEmpty,

  editor: {
    nodes: [OfferNode],
    plugins: [OfferPlugin, OfferAutoDetectPlugin],
  },

  persistence: {
    ownedKeys: [OFFER_OWNED_KEY],
    serialize: (slice) => ({ [OFFER_OWNED_KEY]: slice }),
    deserialize: (raw) => {
      const candidate = raw[OFFER_OWNED_KEY];
      if (candidate === undefined) return undefined;
      const parsed = offerSliceSchema.safeParse(candidate);
      return parsed.success ? parsed.data : undefined;
    },
  },

  createActions: (set) => ({
    setOffer(offer: TextComposerOfferSlice | undefined) {
      set((state) => {
        if (!offer || !offer.slug.trim()) {
          delete state.authoringState.offer;
        } else {
          state.authoringState.offer = offer;
        }
      });
    },
  }),

  frameVariantConfigKeys: ["offers"],

  contributeFrameVariantConfig: (state) => {
    const offer = state.offer;
    if (!offer?.slug) return null;
    return {
      offers: buildOfferConfigSlice(offer.slug, offer.fallbackCode ?? ""),
    };
  },

  transformSms: (state, payload) => {
    const slug = state.offer?.slug;
    if (!slug) return payload;
    const text = replaceOfferPlaceholders(payload.content.body.text, slug);
    if (text === payload.content.body.text) return payload;
    return { content: { body: { type: "TEXT", text } } };
  },

  transformRcs: (state, payload) => {
    const slug = state.offer?.slug;
    if (!slug) return payload;
    const body = payload.content.body;

    if (body.type === "TEXT") {
      const text = replaceOfferPlaceholders(body.text, slug);
      if (text === body.text) return payload;
      return { content: { ...payload.content, body: { type: "TEXT", text } } };
    }

    if (body.type === "IMAGE" && "text" in body && body.text) {
      const text = replaceOfferPlaceholders(body.text, slug);
      if (text === body.text) return payload;
      return { content: { ...payload.content, body: { ...body, text } } };
    }

    return payload;
  },

  transformMms: (state, payload) => {
    const slug = state.offer?.slug;
    if (!slug) return payload;
    const body = payload.content.body;

    if (body.type === "TEXT") {
      const text = replaceOfferPlaceholders(body.text, slug);
      if (text === body.text) return payload;
      return { content: { body: { type: "TEXT", text } } };
    }

    if (body.type === "IMAGE" && "text" in body && body.text) {
      const text = replaceOfferPlaceholders(body.text, slug);
      if (text === body.text) return payload;
      return { content: { body: { ...body, text } } };
    }

    return payload;
  },

  component: OffersSection,
};
